import { ApolloQueryResult } from '@apollo/client';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import {
  EventSubscription,
  Exact,
  ListEventSubscriptionsInput,
  ListEventSubscriptionsQuery,
  useListEventDefinitionsQuery,
  useListEventSubscriptionsQuery,
} from '../graphql/generated';
import { useCurrentUserProp } from '../user.hooks';

type GetChatSubsciptionResult = [
  EventSubscription | undefined,
  (
    variables?:
      | Partial<
          Exact<{
            input: ListEventSubscriptionsInput;
          }>
        >
      | undefined
  ) => Promise<ApolloQueryResult<ListEventSubscriptionsQuery>>,
];

export const useGetChatSubscription = (
  chatId: string
): GetChatSubsciptionResult => {
  const userId = useCurrentUserProp('id');
  const chatDefinitionName = 'chat_notify_me_on_everything';

  const { data: chatEventDefinitionData } = useListEventDefinitionsQuery({
    variables: {
      input: {
        parentId: chatId,
      },
    },
  });

  const chatEventDefinition =
    chatEventDefinitionData?.listEventDefinitions.items?.find(
      (definition) => definition.name === chatDefinitionName
    );

  const { data: eventSubscriptionData, refetch } =
    useListEventSubscriptionsQuery({
      variables: {
        input: {
          eventDefinitionId: chatEventDefinition?.id as string,
        },
      },
      skip: chatEventDefinition === undefined,
    });

  const eventSubscriptions =
    eventSubscriptionData?.listEventSubscriptions.items;

  const existingSubscription = useMemo(() => {
    if (isNil(eventSubscriptions) || eventSubscriptions.length === 0) {
      return;
    }

    return eventSubscriptions.find((sub) => sub?.user?.id === userId);
  }, [eventSubscriptions, userId]);

  return [existingSubscription, refetch];
};
